import React, { useState } from 'react';
import { Box, InputAdornment, Popover, TextField } from '@mui/material';
import IconButton from '@components/Atoms/IconButton/IconButton';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import ShowIf from '@components/Atoms/ShowIf/ShowIf';
import TreeViewSearch from './TreeViewSearch';
import SelectedChips from './SelectedChips';

export type Node = {
  id: string;
  label: string;
  children?: Node[];
  noChip?: boolean;
  noCheckbox?: boolean;
  [key: string]: unknown;
};

export interface MultiSelectTreeProps {
  /** The id of the `MultiSelectTree` */
  id?: string;
  /** Array of objects with { id, label, children: []} */
  tree: Node[];
  /** Whether the `MultiSelectTree` is disabled */
  disabled?: boolean;
  /** The placeholder text */
  placeholder?: string;
  /** Callback fired when the selection changes */
  onChange?: (ids: number[]) => void;
  /** Preselected IDs */
  selected?: number[];
  /** Whether multiple selections are allowed */
  multiple?: boolean;
  /** Callback fired when the Popover is closed */
  onClose?: () => void;
  /** Tags for the Nodes */
  tags?: any;
  /** Background color */
  background?: Record<string, string>;
  /** Array of ids to indicate the selection is indeterminate */
  indeterminate?: number[];
  /** Reference to the parent element */
  parentRef?: React.RefObject<HTMLDivElement> | null;
}

/**
 * A `MultiSelectTree` component
 * @example
    <MultiSelectTree
    tree={[
      {
        id: '1',
        label: 'Parent 1',
        children: [
          { id: '1-1', label: 'Child 1' },
          { id: '1-2', label: 'Child 2' },
        ],
      },
      {
        id: '2',
        label: 'Parent 2',
        children: [
          { id: '2-1', label: 'Child 1' },
          { id: '2-2', label: 'Child 2' },
        ],
      },
    ]}
    onChange={(node) => console.log(node)}
  />

  @returns {React.ReactElement} A `MultiSelectTree` element
 */
const MultiSelectTree = ({
  id,
  tree,
  disabled = false,
  placeholder = 'Click to select items...',
  onChange = undefined,
  selected = [],
  multiple = true,
  onClose,
  tags = {},
  background,
  indeterminate,
  parentRef,
}: MultiSelectTreeProps) => {
  // Anchor Element for the Popover
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);

  // Search Text
  const [searchText, setSearchText] = useState<string>('');

  // we make a string version because the tree only works with string ids
  const selectedIds = selected.map((selectedId) => selectedId.toString());

  // Click to Open Popover
  const handleClick = (event) => {
    event.stopPropagation();
    if (!disabled && parentRef?.current) {
      setAnchorEl(parentRef?.current as HTMLDivElement);
    }
  };

  // Close the Popover
  const handleClose = (event) => {
    event?.stopPropagation();
    setAnchorEl(null);
    onClose?.();
  };

  // Clear the Selected Nodes
  const clearSelection = (event) => {
    event.stopPropagation();
    onChange?.([]);
  };

  const cleanAndReturn = (stringIds: string[]) => {
    const ids = stringIds
      .map((stringId) => parseInt(stringId, 10))
      .filter((numberId) => !Number.isNaN(numberId));
    onChange?.(ids);
  };

  // Remove a Node from the Selected Nodes
  const unselectId = (nodeId: string) => {
    cleanAndReturn(selectedIds.filter((selectedId) => selectedId !== nodeId));
  };

  // combine the selected nodes with the newly selected nodes but make sure they are unique
  const setNodesUnique = (nodes: string[]) => {
    const selectedNodeIds: string[] = [];

    const findNode = (node: Node) => {
      if (nodes.includes(node.id)) {
        selectedNodeIds.push(node.id);
      }
      if (node.children) {
        node.children.forEach((child) => findNode(child));
      }
    };

    tree.forEach((node) => findNode(node));

    cleanAndReturn(selectedNodeIds);
    if (!multiple) {
      handleClose(new Event('click'));
    }
  };

  // Get the Selected Node Objects from the Selected Node IDs
  const getSelectedNodes = (): Node[] => {
    const selectedNodes: Node[] = [];

    const findNode = (node: Node) => {
      if (!node?.noChip && selectedIds.includes(node.id)) {
        selectedNodes.push(node);
      }
      if (node.children) {
        node.children.forEach((child) => findNode(child));
      }
    };

    tree.forEach((node) => findNode(node));

    return selectedNodes;
  };

  return (
    <Box>
      <TextField
        data-testid={id ?? 'multi-select-tree'}
        fullWidth
        disabled={disabled}
        variant="outlined"
        placeholder={selectedIds.length === 0 ? placeholder : ''}
        onClick={handleClick}
        value={!multiple && selectedIds.length > 0 ? getSelectedNodes()?.[0]?.label : ''}
        InputProps={{
          className: 'MuiAutocomplete-inputRoot',
          startAdornment: (
            <ShowIf If={selectedIds.length > 0 && multiple}>
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '0.5rem',
                  maxWidth: '90%',
                  py: '0.5rem',
                }}
              >
                <SelectedChips
                  nodes={getSelectedNodes()}
                  tags={tags}
                  disabled={disabled}
                  onDelete={unselectId}
                  background={background}
                />
              </Box>
            </ShowIf>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <Box className="MuiAutocomplete-endAdornment">
                <ShowIf If={selected.length > 0 && !disabled}>
                  <IconButton onClick={clearSelection} size="small">
                    <CloseIcon />
                  </IconButton>
                </ShowIf>
                <IconButton onClick={handleClick} size="small">
                  <ArrowDropDownIcon />
                </IconButton>
              </Box>
            </InputAdornment>
          ),
          readOnly: true,
        }}
      />
      <Popover
        id="tree-select-popover"
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              p: 1,
              width: 'auto',
              maxWidth: '40rem',
              minWidth: '20rem',
              boxShadow:
                '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)',
            },
          },
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          autoFocus
          sx={{ mb: 1 }}
        />
        <Box
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
          }}
        >
          <TreeViewSearch
            id={`${id}-treeview`}
            tree={tree}
            searchText={searchText}
            selected={selectedIds}
            setSelectedNodes={setNodesUnique}
            multiple={multiple}
            indeterminate={indeterminate?.map((indeterminateId) =>
              indeterminateId.toString(),
            )}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default MultiSelectTree;
